import { Component, OnInit, OnDestroy } from "@angular/core"
import { CsvReader, CsvRow, CsvCell } from 'services/csv-reader'
import { errorToast } from "src/toaster"
import { IContentType } from "src/graphql/queries/keyword.queries"
import { allowedContentTypes } from "../../utils"
import { isDate } from 'src/utils'
import moment from "moment"
import { ActivatedRoute, Router } from "@angular/router"
import { AdminUploadQueries, AdminUploadData, AdminUploadContentType, AdminUploadCustomer } from "./admin-upload.queries"
import { camelCase } from "lodash-es"
import { Subscription } from "rxjs"
import unzip from 'lodash-es/unzip'
import fromPairs from 'lodash-es/fromPairs'
import omit from 'lodash-es/omit'
import { KeywordMutations } from "src/graphql/mutations/keyword.mutations"
import { Keyword } from "src/types"

@Component({
  templateUrl: './admin-upload.page.html',
  styles: [`
    .truncated {
      max-width: 500px;
      max-height: 82px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  `]
})

export class AdminUploadPage implements OnInit, OnDestroy {

  REQUIRED_FIELDS: string[] = ['phrase', 'description', 'contentType']
  OPTIONAL_FIELDS: string[] = [
    'state',
    'writerId',
    'deadline',
    'timeZone',
    'allowInsecureLinks',
    'requireSameTitle',
    'associatedMonth',
    'postLimit',
    'subsId',
    'asanaId',
    'contentRefresh',
    'words',
    'video',
    'photo',
    'optimize' // Could add more here
  ]
  ALLOWED_FIELDS: string[] = this.REQUIRED_FIELDS.concat(this.OPTIONAL_FIELDS)

  customerId: number
  customer: AdminUploadCustomer

  showImportRows = false
  importRows: CsvRow[]
  allowedContentTypes: AdminUploadContentType[]
  contentTypes: AdminUploadContentType[]

  headerErrors: string[] = []
  bodyErrors: string[] = []
  bodyRows: CsvRow[] = []
  headerRow: CsvRow = []
  deadlineIndex: number

  querySub: Subscription
  submitting = false
  resultsReady = false

  uploadedKeywords: number[]
  uploadErrors: string[] = []
  parsedUploadErrors: {
    writer: {
      id: number
      error: string
    }[]
    customer: {
      id: number
      error: string
    }[]
    keyword: {
      id: number
      error: string
    }[]
  } = {writer: [], customer: [], keyword: []}

  KEYWORD_DEFAULTS = {
    state: 'active',
    kind: 'open',
    contentType: 'Blog Post',
    description: ' ',
    postLimit: 1
  }
  KEYWORD_OVERRIDES = {}

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private csvReader: CsvReader,
    private adminUploadQueries: AdminUploadQueries,
    private keywordMutations: KeywordMutations,
  ) { }

  ngOnInit(): void {
    this.route.parent.parent.params.subscribe(customerIdParams => {
      this.customerId = +customerIdParams['customerId']
      this.querySub = this.adminUploadQueries.mainQuery(this.customerId).subscribe(({ data, loading, stale }: { data: AdminUploadData, loading: boolean, stale: boolean }) => {
        if (data) {
          this.contentTypes = data.contentTypes
          this.customer = data.customer
          if (this.customer.multipass) {
            this.allowedContentTypes = this.contentTypes
          } else {
            this.allowedContentTypes = allowedContentTypes(this.contentTypes, this.customer.plan.words)
          }
        }
      })
    })

  }

  getColumnIndexFromHeader(headerStr: string): number {
    return this.headerRow.findIndex(c => c === headerStr)
  }

  openFileSelector(fileInputId: string): void {
    const uploader = document.getElementById(fileInputId)
    if (uploader) uploader.click()
  }

  parseImports(inputTarget: HTMLInputElement): void {
    const files: FileList = inputTarget.files
    this.csvReader.readRows(files[0])
      .then((rows: CsvRow[]) => {
        this.importRows = rows
        this.headerRow = this.importRows[0]?.map(header => camelCase(header)) || []
        this.bodyRows = this.importRows?.slice(1) || []
        this.deadlineIndex = this.getColumnIndexFromHeader('deadline')
        this.headerRow.forEach(headerData => {
          if (this.headerHasError(headerData, this.ALLOWED_FIELDS)) this.headerErrors.push(`${headerData} is not a valid column`)
        })
        this.bodyRows.forEach((bodyRow, rowIndex) => {
          if (bodyRow.length !== this.headerRow.length) this.bodyErrors.push(`Row ${rowIndex + 1} is missing data`)
          bodyRow.forEach((bodyData, columnIndex) => {
            const error = this.cellError(bodyData, columnIndex, rowIndex)
            if (error) this.bodyErrors.push(error)
          })
        })
        this.showImportRows = true
      })
      .catch((error: string) => {
        errorToast(error)
      })
  }

  headerHasError(header: string, allowedFields: string[]): boolean {
    return !allowedFields.includes(header)
  }

  cellError(data: CsvCell, colindex: number, rowindex: number): string {
    const column = this.headerRow[colindex]
    if (!data && this.REQUIRED_FIELDS.includes(column)) {
      return 'no data provided'
    } else if (
      column === 'contentType' &&
      this.contentTypeNotAllowed(data.toLowerCase(), this.allowedContentTypes.map(c => c.name.toLowerCase()))) {
      return `"${data}" is an invalid content type`
    } else if (column === 'deadline' && data && !isDate(data)) {
      return `is not a valid time - please try a time like '2020-06-30 15:30'`
    } else if (column === 'timeZone' && data && !moment.tz.zone(data)) {
      return `${data} is not a valid time zone`
    } else if (!this.headerRow[colindex]) {
      return `needs a specified header`
    } else if (column === 'writerId' && data) {
      let errorMessage = ''
      const deadlineData = this.bodyRows[rowindex][this.deadlineIndex]
      if (this.deadlineIndex < 0 || !deadlineData) {
        errorMessage = `Assigned keywords must have a deadline`
      }
      if (this.uploadError('writer', +data)) errorMessage += ' - ' + this.uploadError('writer', +data)
      return errorMessage
    } else if (column === 'subsId' && data) {
      let errorMessage = ''
      if (!Number.isInteger(+data)) {
        errorMessage += 'has an invalid character'
      }
      if(this.uploadError('customer', +data)) errorMessage += ' - ' + this.uploadError('customer', +data)
      return errorMessage
    }
  }

  uploadError(context: 'writer' | 'customer' | 'keyword', id: number): string {
    return this.parsedUploadErrors[context].find(e => e.id === id)?.error
  }

  contentTypeNotAllowed(data: CsvCell, allowedContentTypes: string[]): boolean {
    return !allowedContentTypes.includes(data.toLowerCase())
  }

  clearFile(): void {
    this.showImportRows = false
    const keywordUploaderElement = document.getElementById('keywordUploader') as HTMLInputElement
    if (keywordUploaderElement) keywordUploaderElement.value = ''
    this.bodyErrors = []
    this.headerErrors = []
    this.uploadedKeywords = []
    this.uploadErrors = []
    this.parsedUploadErrors = {
      writer: [],
      customer: [],
      keyword: []
    }
    this.resultsReady = false
    this.submitting = false
  }

  createKeywords(headerRow: CsvRow, bodyRows: CsvRow[]): object[] {
    return bodyRows.map(bodyRow => this.createKeyword(headerRow, bodyRow))
  }

  createKeyword(headerRow: CsvRow, bodyRow: CsvRow): object {
    const pairs = unzip([headerRow, bodyRow])
    const keyword = fromPairs(pairs)
    if (keyword.deadline) {
      keyword.kind = 'deadline'
      const tz = keyword.timeZone || 'America/Denver'
      keyword.deadline = moment.tz(keyword.deadline, tz).format()
      keyword.timeZone = tz
    }
    if (keyword.writerId) {
      keyword.kind = 'assigned'
      keyword.state = 'assigned'
    }
    if (keyword.state === 'proposed') {
      keyword.kind = 'proposed'
    }
    if (keyword.state === 'proposed') {
      keyword.kind = 'proposed'
    }
    keyword.subsId = keyword.subsId || this.customerId
    return keyword
  }

  onSubmit(): void {
    if (this.headerErrors.length === 0 && this.bodyErrors.length === 0) {
      this.submitting = true
      const keywords = this.createKeywords(this.headerRow, this.bodyRows).map(k => {
        const keyword = { ...this.KEYWORD_DEFAULTS, ...k, ...this.KEYWORD_OVERRIDES }
        const contentType = this.allowedContentTypes.find(c => c.name.toLowerCase() === keyword.contentType.toLowerCase())
        return omit({ ...keyword, contentTypeId: contentType && contentType.id }, 'contentType')
      })
      this.createBatchKeywords(keywords)
    }
  }

  createBatchKeywords = async (keywords: object[]) => {
    const data = await this.keywordMutations.createKeywords({ keywords })

    if (data.errors.length) {
      this.uploadErrors = data.errors
      this.parseUploadErrors()
    } else {
      this.uploadedKeywords = data.keywords
    }

    this.resultsReady = true
    this.submitting = false
  }

  parseUploadErrors(): void {
    this.uploadErrors.forEach(error => {
      const parsedError = error.split('-')
      const context = parsedError[0].replace(/\s+/g, '').toLowerCase()
      const id = +parsedError[1]?.replace(/\s+/g, '')
      this.parsedUploadErrors[context].push({
        id,
        error: parsedError[2]
      })
    })
  }

  disableImportButton(): boolean {
    return this.headerErrors.length > 0
      || this.bodyErrors.length > 0
      || this.submitting
      || this.parsedUploadErrors.customer.length > 0
      || this.parsedUploadErrors.writer.length > 0
      || this.parsedUploadErrors.keyword.length > 0
  }

  ngOnDestroy(): void {
    if (this.querySub) this.querySub.unsubscribe
  }

}
